import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
// Utility Allocation Export — native (non-embedded) rebuild of Metabase dashboard #101
// ("Bungalow — Utility Allocation Export"). NO Metabase at runtime.
//
// Preferred source is the allocations API's own export endpoint (Reports/utility-allocation-export),
// which does the whole join server-side and is the only source of tenant charge detail. Until that
// endpoint is deployed everywhere we fall back to deriving the report client-side from
// Allocations/billsAllocations + AllocationCycle history, which yields everything except tenant rows.
//
// We call the api layer directly rather than the MST store actions: `getBillsAsync` and the history
// actions overwrite shared allocation state that the Allocations page reads.

import { api } from 'src/api';
const NO_OPTIONS = {
  cancelToken: undefined
};

// Current company GUID = JWT custom:companyid (authoritative; same id the backend scopes to).
// Under admin impersonation the refreshed token carries the impersonated company, so this is the
// customer's id, not the admin's. Guards against the "null"/"undefined" string sentinels
// localStorage/JWT can carry.
export const currentCompanyId = () => {
  const clean = v => v && v !== 'null' && v !== 'undefined' ? v : null;
  try {
    const t = localStorage.getItem('livable_access_token');
    if (t) {
      const id = clean(JSON.parse(atob(t.split('.')[1]))['custom:companyid']);
      if (id) return id;
    }
  } catch (e) {
    /* fall through */
  }
  return clean(localStorage.getItem('livable_cmpany_name'));
};
const round2 = n => Math.round((Number(n) || 0) * 100) / 100;
const num = v => v === null || v === undefined ? null : Number(v);
const date10 = d => d ? String(d).slice(0, 10) : '';
const period = (a, b) => a && b ? "".concat(date10(a), " - ").concat(date10(b)) : '';
const monthLabel = (year, month) => new Date(year, month - 1, 1).toLocaleDateString('en-US', {
  month: 'short',
  year: '2-digit'
});
const cycleLabelOf = (year, month) => new Date(year, month - 1, 1).toLocaleDateString('en-US', {
  month: 'long',
  year: 'numeric'
});
export const EMPTY_REPORT = {
  cycleId: null,
  cycleLabel: '',
  cycleIsClosed: false,
  cycles: [],
  hasTenantDetail: false,
  kpis: {
    totalInvoice: 0,
    totalAllocated: 0,
    ownerBillable: 0,
    recoveryPct: 0,
    invoiceCount: 0
  },
  coverage: [],
  byService: [],
  topProperties: [],
  propertySummary: [],
  invoices: []
};

// Group + sum variance, keep only owner-billable (>0), sorted desc. Shared by the
// "Owner-Billable by Service" and "Top Properties" charts (Metabase cards #340 / #341).
const ownerBillableBy = (rows, keyFn) => {
  const m = new Map();
  rows.forEach(r => {
    const k = keyFn(r);
    m.set(k, (m.get(k) || 0) + (Number(r.variance) || 0));
  });
  return [...m.entries()].map(_ref => {
    let _ref2 = _slicedToArray(_ref, 2),
      label = _ref2[0],
      variance = _ref2[1];
    return {
      label,
      variance: round2(variance)
    };
  }).filter(d => d.variance > 0).sort((a, b) => b.variance - a.variance);
};

// KPIs and both breakdown charts are derived from the invoice rows, so the server-backed and the
// client-derived paths render through exactly one code path.
function derive(base) {
  const b = _objectSpread(_objectSpread({}, EMPTY_REPORT), base);
  const invoices = b.invoices;
  const totalInvoice = round2(invoices.reduce((s, r) => s + (r.total_bill_amount || 0), 0));
  const totalAllocated = round2(invoices.reduce((s, r) => s + (r.total_allocated || 0), 0));
  return _objectSpread(_objectSpread({}, b), {}, {
    kpis: {
      totalInvoice,
      totalAllocated,
      ownerBillable: round2(totalInvoice - totalAllocated),
      recoveryPct: totalInvoice ? totalAllocated / totalInvoice : 0,
      invoiceCount: invoices.length
    },
    byService: ownerBillableBy(invoices, r => r.service || '(unmapped)').map(d => ({
      service: d.label,
      variance: d.variance
    })),
    topProperties: ownerBillableBy(invoices, r => r.property_address).slice(0, 10).map(d => ({
      property: d.label,
      variance: d.variance
    }))
  });
}
const sortInvoices = rows => rows.sort((a, b) => a.property_address.localeCompare(b.property_address) || a.service.localeCompare(b.service));

// ---------------------------------------------------------------------------
// Preferred path: the allocations API computes the whole report.
// ---------------------------------------------------------------------------

function fromServerExport(payload, cycles) {
  var _payload$cycle$id, _payload$cycle, _payload$cycle$label, _payload$cycle2, _payload$cycle3;
  const invoices = (payload.invoices || []).map(i => ({
    property_address: i.propertyAddress || '',
    service: i.service || '',
    total_bill_amount: num(i.totalBillAmount),
    total_allocated: Number(i.totalAllocated) || 0,
    variance: num(i.variance),
    bill_period: period(i.billPeriodStart, i.billPeriodEnd),
    active_residents: Number(i.activeUnits) || 0,
    is_skipped: !!i.isSkipped,
    tenants: (i.tenants || []).map(t => ({
      unit: t.unitName || '',
      name: t.residentName || '',
      charge: Number(t.allocatedAmount) || 0
    }))
  }));
  return derive({
    cycleId: (_payload$cycle$id = (_payload$cycle = payload.cycle) === null || _payload$cycle === void 0 ? void 0 : _payload$cycle.id) !== null && _payload$cycle$id !== void 0 ? _payload$cycle$id : null,
    cycleLabel: (_payload$cycle$label = (_payload$cycle2 = payload.cycle) === null || _payload$cycle2 === void 0 ? void 0 : _payload$cycle2.label) !== null && _payload$cycle$label !== void 0 ? _payload$cycle$label : '',
    cycleIsClosed: !!((_payload$cycle3 = payload.cycle) !== null && _payload$cycle3 !== void 0 && _payload$cycle3.isClosed),
    cycles,
    hasTenantDetail: true,
    invoices: sortInvoices(invoices),
    propertySummary: (payload.propertySummary || []).map(p => ({
      // Carried so the tab can offer a property filter; the address alone is not a stable key.
      property_id: p.propertyId || null,
      property_address: p.propertyAddress || '',
      utilities_configured: Number(p.utilitiesConfigured) || 0,
      utilities_billed_this_period: Number(p.utilitiesBilled) || 0,
      invoices_in_period: invoices.filter(i => i.property_address === p.propertyAddress).length,
      total_invoiced: Number(p.invoiced) || 0,
      total_allocated: Number(p.allocated) || 0,
      owner_billable: Number(p.variance) || 0
    })),
    coverage: (payload.coverage || []).map(c => ({
      month: monthLabel(c.year, c.month),
      invoices: Number(c.invoiceCount) || 0
    }))
  });
}

// ---------------------------------------------------------------------------
// Fallback path: derive from the endpoints the app already had.
// ---------------------------------------------------------------------------

// The real per-utility shape of Allocations/billsAllocations is
// AllocationSummaryResponse.UtilitySummaryResponse: { utilityId, billValue, total, serviceStartDateOnly,
// serviceEndDateOnly, billStatus, activeUnits, totalOccupants, isApproved, isSkipped }.
const BILL_COMPLETE_STATUSES = ['TableReady', 'TableReadyStatementSent'];
const isCycleClosed = byProperty => byProperty.length > 0 && byProperty.every(p => (p.utilities || []).every(u => u.isSkipped || BILL_COMPLETE_STATUSES.includes(u.billStatus)));

// At least one uploaded bill. A cycle where every utility is skipped is "closed" but has nothing to
// report, so it must not be the default landing cycle (that reproduced the all-$0.00 view).
const hasRealBill = byProperty => byProperty.some(p => (p.utilities || []).some(u => num(u.billValue) !== null));
async function fetchBills(allocationCycleId, propertyIds) {
  const resp = await api.Allocations.getBillsAllocations({
    allocationCycleId,
    propertyIds
  }, NO_OPTIONS);
  return resp !== null && resp !== void 0 && resp.isSuccessful ? resp.response || [] : [];
}

// Utility id -> utility type name ("Water"). billsAllocations only carries utilityId, so without this
// every invoice groups under a blank service and the by-service chart cannot render.
async function fetchServiceNames(utilityIds) {
  var _resp$response;
  if (!utilityIds.length) return {};
  const resp = await api.UtilityProviders.getUtilityInfo({
    utilityIds
  }, NO_OPTIONS);
  const utilities = resp !== null && resp !== void 0 && resp.isSuccessful ? ((_resp$response = resp.response) === null || _resp$response === void 0 ? void 0 : _resp$response.utilities) || [] : [];
  return Object.fromEntries(utilities.map(u => [u.utilityId, u.utilityTypeName || '']));
}
function invoiceRowsFrom(byProperty, addressById, serviceNames) {
  const rows = [];
  byProperty.forEach(p => {
    (p.utilities || []).forEach(u => {
      const billed = num(u.billValue);
      const allocated = Number(u.total) || 0;
      rows.push({
        property_address: addressById[p.propertyId] || '',
        service: serviceNames[u.utilityId] || '',
        total_bill_amount: billed,
        total_allocated: allocated,
        variance: billed === null ? null : round2(billed - allocated),
        bill_period: period(u.serviceStartDateOnly, u.serviceEndDateOnly),
        active_residents: Number(u.activeUnits) || 0,
        is_skipped: !!u.isSkipped,
        tenants: []
      });
    });
  });
  return sortInvoices(rows);
}
function propertySummaryFrom(properties, invoices) {
  return properties.map(p => {
    const address = p.propertyAddress || p.propertyName || '';
    const rows = invoices.filter(i => i.property_address === address);
    const invoiced = round2(rows.reduce((s, r) => s + (r.total_bill_amount || 0), 0));
    const allocated = round2(rows.reduce((s, r) => s + r.total_allocated, 0));
    return {
      property_id: p.id || p.propertyId || null,
      property_address: address,
      utilities_configured: (p.utilities || []).length,
      utilities_billed_this_period: rows.filter(r => r.total_bill_amount !== null).length,
      invoices_in_period: rows.length,
      total_invoiced: invoiced,
      total_allocated: allocated,
      owner_billable: round2(invoiced - allocated)
    };
  }).sort((a, b) => a.property_address.localeCompare(b.property_address));
}

// Invoice volume per cycle. billsAllocations is per (cycle, properties), so this costs one request
// per cycle — the reason the server-side endpoint exists.
async function coverageFrom(cycles, propertyIds) {
  const counts = await Promise.all(cycles.map(async c => {
    const byProperty = await fetchBills(c.id, propertyIds);
    const invoices = byProperty.reduce((n, p) => n + (p.utilities || []).filter(u => num(u.billValue) !== null).length, 0);
    return {
      month: monthLabel(c.year, c.month),
      invoices
    };
  }));
  return counts.reverse(); // cycles arrive newest-first; charts read oldest-first
}
async function fromClientDerivation(store, companyId, requestedCycleId) {
  let requestedPropertyIds = arguments.length > 3 && arguments[3] !== undefined ? arguments[3] : null;
  const propsResp = await store.company.getCompanyPropertyListAsync.run();
  let properties = (propsResp === null || propsResp === void 0 ? void 0 : propsResp.response) || [];

  // The filter has to narrow here too. This path runs when the server export fails or is not
  // deployed yet, and without it the user's property selection is silently dropped - they pick one
  // property and the CSV comes back with every one of them, with nothing to say it ignored them.
  if (requestedPropertyIds !== null && requestedPropertyIds !== void 0 && requestedPropertyIds.length) {
    const requested = new Set(requestedPropertyIds);
    properties = properties.filter(p => requested.has(p.propertyId));
  }
  const propertyIds = properties.map(p => p.propertyId).filter(Boolean);
  if (!propertyIds.length) return derive({});
  const addressById = Object.fromEntries(properties.map(p => [p.propertyId, p.propertyAddress || p.propertyName || '']));
  const historyResp = await api.Allocations.getAllocationsHistory({
    companyId
  }, NO_OPTIONS);
  const history = (historyResp !== null && historyResp !== void 0 && historyResp.isSuccessful ? historyResp.response || [] : []).slice().sort((a, b) => b.year - a.year || b.month - a.month);
  if (!history.length) return derive({});
  const cycles = history.map(c => ({
    id: c.id,
    year: c.year,
    month: c.month,
    label: cycleLabelOf(c.year, c.month)
  }));
  const recent = history.slice(0, 12);

  // Default to the most recent cycle that actually has bills, preferring a closed one. The open
  // cycle has no completed bills and a skipped-only cycle has none at all — either rendered $0.00.
  let selected = requestedCycleId ? history.find(c => c.id === requestedCycleId) : null;
  let byProperty = selected ? await fetchBills(selected.id, propertyIds) : [];
  if (!selected) {
    let closedWithBills = null;
    let anyWithBills = null;
    for (const candidate of recent) {
      const bills = await fetchBills(candidate.id, propertyIds);
      if (!hasRealBill(bills)) continue;
      anyWithBills !== null && anyWithBills !== void 0 ? anyWithBills : anyWithBills = {
        candidate,
        bills
      };
      if (isCycleClosed(bills)) {
        closedWithBills = {
          candidate,
          bills
        };
        break; // newest-first, so the first closed-with-bills is the one we want
      }
    }
    const pick = closedWithBills !== null && closedWithBills !== void 0 ? closedWithBills : anyWithBills;
    if (pick) {
      selected = pick.candidate;
      byProperty = pick.bills;
    } else {
      selected = history[0];
      byProperty = await fetchBills(selected.id, propertyIds);
    }
  }
  const utilityIds = [...new Set(byProperty.flatMap(p => (p.utilities || []).map(u => u.utilityId)))];
  const serviceNames = await fetchServiceNames(utilityIds);
  const invoices = invoiceRowsFrom(byProperty, addressById, serviceNames);
  return derive({
    cycleId: selected.id,
    cycleLabel: cycleLabelOf(selected.year, selected.month),
    cycleIsClosed: isCycleClosed(byProperty),
    cycles,
    hasTenantDetail: false,
    invoices,
    propertySummary: propertySummaryFrom(properties, invoices),
    coverage: await coverageFrom(recent, propertyIds)
  });
}

/**
 * Build the Utility Allocation Export for the current (possibly impersonated) company.
 * @param {object} store root MST store — only used for the company property list
 * @param {string|null} allocationCycleId cycle to report on; null selects the latest closed cycle
 * @param {string[]|null} propertyIds restrict to these properties; null/empty means all of them.
 *   The server intersects this with the caller's authorized set, so it can only narrow.
 */
export async function fetchUtilityAllocationExport(store) {
  let allocationCycleId = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : null;
  let propertyIds = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : null;
  const companyId = currentCompanyId();
  if (!companyId) return derive({});
  try {
    var _resp$response2;
    const resp = await api.Allocations.getUtilityAllocationExport({
      allocationCycleId,
      monthsBack: 12,
      propertyIds
    }, NO_OPTIONS);
    if (resp !== null && resp !== void 0 && resp.isSuccessful && (_resp$response2 = resp.response) !== null && _resp$response2 !== void 0 && _resp$response2.cycle) {
      const histResp = await api.Allocations.getAllocationsHistory({
        companyId
      }, NO_OPTIONS);
      const cycles = (histResp !== null && histResp !== void 0 && histResp.isSuccessful ? histResp.response || [] : []).slice().sort((a, b) => b.year - a.year || b.month - a.month).map(c => ({
        id: c.id,
        year: c.year,
        month: c.month,
        label: cycleLabelOf(c.year, c.month)
      }));
      return fromServerExport(resp.response, cycles);
    }
  } catch (e) {
    // Endpoint not deployed on this environment yet — derive it client-side instead.
  }
  return fromClientDerivation(store, companyId, allocationCycleId, propertyIds);
}